import React, { useState } from 'react';
import { CheckoutConfig, PlanDetail } from '../types';
import { useSettings } from './SettingsContext';
import { INITIAL_PLANS } from '../services/mockData';
import { CheckoutStudioPreview } from './checkout/studio/CheckoutStudioPreview';
import { CheckoutStudioTemplatesTab } from './checkout/studio/CheckoutStudioTemplatesTab';
import { CheckoutStudioVisualTab } from './checkout/studio/CheckoutStudioVisualTab';
import { CheckoutStudioAddonsTab } from './checkout/studio/CheckoutStudioAddonsTab';
import { CheckoutStudioFieldsTab } from './checkout/studio/CheckoutStudioFieldsTab';
import { CheckoutStudioTrustTab } from './checkout/studio/CheckoutStudioTrustTab';
import { CheckoutStudioCroTab } from './checkout/studio/CheckoutStudioCroTab';
import { CheckoutStudioFlowTab } from './checkout/studio/CheckoutStudioFlowTab';
import { CheckoutStudioAbTab } from './checkout/studio/CheckoutStudioAbTab';
import { CheckoutStudioShareModal } from './checkout/studio/CheckoutStudioShareModal';

type TabType = 'templates' | 'visual' | 'addons' | 'fields' | 'trust' | 'cro' | 'flow' | 'ab';

const TABS: { id: TabType; label: string }[] = [
  { id: 'templates', label: 'Templates' },
  { id: 'visual', label: 'Visual & Theme' },
  { id: 'addons', label: 'Add-ons & Discounts' },
  { id: 'fields', label: 'Form Fields' },
  { id: 'trust', label: 'Trust & Security' },
  { id: 'cro', label: 'Urgency & CRO' },
  { id: 'flow', label: 'Funnel Flow' },
  { id: 'ab', label: 'A/B Testing' },
];

const CheckoutCustomization: React.FC = () => {
  const { settings, updateSettings } = useSettings();
  const config = settings.checkoutConfig;

  const [activeTab, setActiveTab] = useState<TabType>('templates');
  const [selectedPlan, setSelectedPlan] = useState<PlanDetail>(INITIAL_PLANS[2]);
  const [previewVariation, setPreviewVariation] = useState<{ testId: string; varId: string } | null>(null);
  const [mobileView, setMobileView] = useState<'editor' | 'preview'>('editor');
  const [showShareModal, setShowShareModal] = useState(false);

  const handleUpdate = (partial: Partial<CheckoutConfig>) => {
    updateSettings({ checkoutConfig: { ...config, ...partial } });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-white dark:bg-slate-950 overflow-hidden">
      {/* Studio Header Toolbar */}
      <div className="px-4 py-2.5 bg-slate-900 border-b border-slate-800 flex items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div>
            <h1 className="text-sm font-bold text-white leading-tight">Checkout Studio</h1>
            <p className="text-[11px] text-slate-400 leading-tight">Customize high-converting hosted checkout experiences</p>
          </div>
        </div>

        {/* Responsive Mobile/Desktop View Switcher & Actions */}
        <div className="flex items-center gap-2">
          <div className="lg:hidden flex bg-slate-800 p-0.5 rounded-lg border border-slate-700">
            <button
              type="button"
              onClick={() => setMobileView('editor')}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                mobileView === 'editor' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              Controls
            </button>
            <button
              type="button"
              onClick={() => setMobileView('preview')}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                mobileView === 'preview' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              Live Preview
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowShareModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg shadow-sm transition-colors"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" x2="12" y1="2" y2="15" />
            </svg>
            <span>Share & Embed</span>
          </button>
        </div>
      </div>

      {/* Main Studio Body: Side-by-Side on Desktop, Tabbed on Mobile/Tablet */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* Left Side: Editor Form Controls */}
        <div
          className={`w-full lg:w-[480px] xl:w-[520px] shrink-0 border-r border-slate-200 dark:border-slate-800 flex flex-col bg-white dark:bg-slate-950 ${
            mobileView === 'preview' ? 'hidden lg:flex' : 'flex'
          }`}
        >
          {/* Tabs Navigation */}
          <div className="flex overflow-x-auto border-b border-slate-200 dark:border-slate-800 px-2 pt-2 bg-slate-50 dark:bg-slate-900/40 custom-scrollbar shrink-0">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 text-xs font-bold whitespace-nowrap border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-slate-900 rounded-t-lg'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content Body */}
          <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
            {activeTab === 'templates' && <CheckoutStudioTemplatesTab config={config} onUpdate={handleUpdate} />}
            {activeTab === 'visual' && <CheckoutStudioVisualTab config={config} onUpdate={handleUpdate} />}
            {activeTab === 'addons' && <CheckoutStudioAddonsTab config={config} onUpdate={handleUpdate} />}
            {activeTab === 'fields' && <CheckoutStudioFieldsTab config={config} onUpdate={handleUpdate} />}
            {activeTab === 'trust' && <CheckoutStudioTrustTab config={config} onUpdate={handleUpdate} />}
            {activeTab === 'cro' && <CheckoutStudioCroTab config={config} onUpdate={handleUpdate} />}
            {activeTab === 'flow' && <CheckoutStudioFlowTab config={config} onUpdate={handleUpdate} />}
            {activeTab === 'ab' && (
              <CheckoutStudioAbTab
                config={config}
                onUpdate={handleUpdate}
                previewVariation={previewVariation}
                onSelectPreviewVariation={setPreviewVariation}
              />
            )}
          </div>
        </div>

        {/* Right Side: Live Interactive Viewport Simulator */}
        <div
          className={`flex-1 min-h-0 bg-slate-950 flex-col overflow-hidden ${
            mobileView === 'editor' ? 'hidden lg:flex' : 'flex'
          }`}
        >
          <CheckoutStudioPreview
            config={config}
            previewVariation={previewVariation}
            onClearVariationPreview={() => setPreviewVariation(null)}
            selectedPlan={selectedPlan}
            onChangePlan={setSelectedPlan}
          />
        </div>
      </div>

      {/* Share & Embed Modal */}
      {showShareModal && (
        <CheckoutStudioShareModal
          config={config}
          selectedPlan={selectedPlan}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default CheckoutCustomization;
